import {Box} from '@primer/react'
import {graphql} from 'react-relay'
import {useFragment} from 'react-relay/hooks'

import {Ago} from './row/Ago'
import {LABELS} from '../constants'
import {createIssueEventExternalUrl} from '../utils/urls'
import type {ReferencedEvent$key} from './__generated__/ReferencedEvent.graphql'
import {TimelineRow} from './row/TimelineRow'
import {ReferencedEventInner} from './ReferencedEventInner'

type ReferencedEventProps = {
  queryRef: ReferencedEvent$key
  nextQueryRefs?: ReferencedEvent$key[]
  issueUrl: string
  highlightedEventId?: string
  refAttribute?: React.MutableRefObject<HTMLDivElement | null>
}

export const ReferencedEventFragment = graphql`
  fragment ReferencedEvent on ReferencedEvent {
    databaseId
    actor {
      ...EventActor
      ...EventActorAvatarItemBadge
    }
    commit {
      message
      url
      abbreviatedOid
      repository {
        name
        owner {
          login
        }
      }
    }
    createdAt
  }
`

export function ReferencedEvent({
  queryRef,
  issueUrl,
  highlightedEventId,
  refAttribute,
  nextQueryRefs,
}: ReferencedEventProps): JSX.Element {
  const {actor, createdAt, commit, databaseId} = useFragment(ReferencedEventFragment, queryRef)

  if (commit === null) {
    return <></>
  }

  const highlighted = String(databaseId) === highlightedEventId
  const numItems = 1 + (nextQueryRefs?.length ?? 0)

  return (
    <TimelineRow
      highlighted={highlighted}
      refAttribute={refAttribute}
      actor={actor}
      createdAt={createdAt}
      actorAvatar={actor}
      deepLinkUrl={issueUrl}
      showAgoTimestamp={false}
      leadingIconOnTop={true}
    >
      <>
        {`${LABELS.timeline.addedCommitsThatReferences(numItems)} `}
        <Ago timestamp={new Date(createdAt)} linkUrl={createIssueEventExternalUrl(issueUrl, databaseId)} />
        <Box sx={{display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '2px'}}>
          {[queryRef, ...(nextQueryRefs ?? [])].map((itemQueryRef, index) => (
            <ReferencedEventInner queryRef={itemQueryRef} key={`${databaseId}_${index}`} />
          ))}
        </Box>
      </>
    </TimelineRow>
  )
}

try{ ReferencedEvent.displayName ||= 'ReferencedEvent' } catch {}